import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BookHeart, ChevronLeft, ChevronRight, Download, Share2 } from "lucide-react";
import { api, downloadFile, errMsg } from "@/lib/api";
import { ShareStoryDialog } from "@/components/insights/ShareStoryDialog";
import { logError } from "@/lib/log";

const shift = (key, by) => {
  const [y, m] = key.split("-").map(Number);
  const d = new Date(y, m - 1 + by, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const change = (e, hadPrev) => {
  if (!hadPrev) return "";
  const d = e.count - e.prev;
  return !e.prev ? "new" : d === 0 ? "same" : `${d > 0 ? "+" : "−"}${Math.abs(d)}`;
};

const Highlights = ({ s }) => {
  const items = [
    s.new_top && ["insight-story-new-top", `${s.new_top} took the top spot — a shift from the month before.`],
    s.toughest_day && ["insight-story-toughest", `${s.toughest_day} tended to feel heaviest.`],
    s.best_day && ["insight-story-best-day", `${s.best_day} were your brightest days.`],
    s.best_skill && ["insight-story-skill", `${s.best_skill.name} helped most — stress dropped ${s.best_skill.avg_drop} on average (${s.best_skill.uses}×).`],
  ].filter(Boolean);
  return items.length ? <ul className="mt-4 space-y-1.5 text-sm list-disc pl-5">{items.map(([id, t]) => <li key={id} data-testid={id}>{t}</li>)}</ul> : null;
};

const Emotions = ({ s }) => (
  <div className="mt-4 divide-y">
    {s.top_emotions.map((e) => (
      <div key={e.name} className="flex items-center gap-3 py-2 text-sm">
        <span className="text-xl w-7">{e.emoji}</span><span className="flex-1 font-semibold">{e.name}</span>
        <span className="text-mm-muted">{e.count}×</span>
        <span className="text-xs w-12 text-right text-mm-primary font-semibold">{change(e, s.had_prev)}</span>
      </div>
    ))}
  </div>
);

export const MonthlyStory = () => {
  const [month, setMonth] = useState(null);
  const [s, setS] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [dl, setDl] = useState(false);

  useEffect(() => {
    api.get("/monthly-story", { params: month ? { month } : {} }).then((r) => setS(r.data)).catch((e) => logError("Monthly story failed:", e));
  }, [month]);

  const pdf = async () => {
    setDl(true);
    try { await downloadFile(`/monthly-story/pdf?month=${s.month_key}`, `mindfulme-story-${s.month_key}.pdf`); toast.success("PDF downloaded"); }
    catch (e) { toast.error(errMsg(e, "Could not make the PDF")); } finally { setDl(false); }
  };

  if (!s) return null;
  const btn = "p-2 rounded-full hover:bg-mm-soft disabled:opacity-30 transition-colors";
  return (
    <div className="mm-card p-5" data-testid="insight-monthly-story">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-semibold flex items-center gap-2 whitespace-nowrap"><BookHeart size={18} /> Emotion story</div>
        <div className="flex items-center gap-1">
          <button className={btn} disabled={s.month_key <= s.earliest} onClick={() => setMonth(shift(s.month_key, -1))} data-testid="insight-story-prev" aria-label="Previous month"><ChevronLeft size={18} /></button>
          <span className="text-sm font-semibold min-w-[110px] text-center whitespace-nowrap" data-testid="insight-story-month">{s.month}</span>
          <button className={btn} disabled={s.month_key >= s.latest} onClick={() => setMonth(shift(s.month_key, 1))} data-testid="insight-story-next" aria-label="Next month"><ChevronRight size={18} /></button>
        </div>
      </div>
      {s.checkins ? (
        <>
          <p className="text-sm text-mm-muted mt-1" data-testid="insight-story-checkins">{s.checkins} check-in{s.checkins === 1 ? "" : "s"} {s.current ? "so far this month" : `in ${s.month}`}</p>
          <Emotions s={s} />
          <Highlights s={s} />
          <div className="flex flex-wrap gap-2 mt-5">
            <button className="mm-btn-ghost !py-2 !px-4 text-sm inline-flex items-center gap-2" disabled={dl} onClick={pdf} data-testid="insight-story-pdf"><Download size={15} /> {dl ? "Preparing…" : "Download PDF"}</button>
            <button className="mm-btn-ghost !py-2 !px-4 text-sm inline-flex items-center gap-2" onClick={() => setSharing(true)} data-testid="insight-story-share"><Share2 size={15} /> Share with a supporter</button>
          </div>
          {sharing && <ShareStoryDialog payload={{ month: s.month_key }} label={`${s.month} story`} onClose={() => setSharing(false)} />}
        </>
      ) : <p className="text-sm text-mm-muted mt-3" data-testid="insight-story-empty">{s.current ? "No check-ins yet this month. Your story will appear once you check in." : `No check-ins in ${s.month}.`}</p>}
    </div>
  );
};
